import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";

import { AuthService } from "../../insa-services";
import { appConfig } from "../../../insaweb.config";
import {
  ControlTable,
  ControlTableRow,
  ControlTableSecurity,
} from "./control-table.models";

export interface TraderSecurityDtoModel {
  SecIndex: number;
  SecNumber: string;
  SecISIN: string;
  SecStockExchange: number;
  SecSymbol: string;
  SecName: string;
  SecCurrSymbol: string;
  FxRate: number;
  StockExchangeCode: string;
  StockExchangeName: string;
  EODHDStockExchangeCode: string;
  SecType: number;
  SecCurr: number;
  SecFactor: number;
  SecContractSize: number;
  fromDate?: string;
  toDate?: string;
}

export interface EODHDRealTimeResponseModel {
  price: number | null;
  bid: number | null;
  bidQuantity: number | null;
  ask: number | null;
  askQuantity: number | null;
  level: number | null;
  lastClose: number | null;
}

export interface EODHDEODResponseModel {
  date: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  adjusted_close: number | null;
  volume: number | null;
}

export interface TraderOrderResponseModel {
  success: boolean;
  description: string;
}

export interface TraderCheckOrderResponseModel {
  success: boolean;
  description: string;
  amounts: { [key: string]: number };
  commission?: number;
  expenses?: number;
  otherExpenses?: number;
}

@Injectable()
export class ControlTableService {
  private readonly apiBase = appConfig.insaServiceAddress + "/Trader";
  private readonly tables$ = new BehaviorSubject<ControlTable[]>([]);

  constructor(
    private _http: HttpClient,
    private _authService: AuthService,
  ) {}

  private get storageKey(): string {
    const user = (this._authService.username || "anon").toLowerCase();
    return `insa_control_tables_${user}`;
  }

  load(): void {
    let tables: ControlTable[] = [];
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (raw) tables = JSON.parse(raw) as ControlTable[];
    } catch {
      tables = [];
    }
    if (!Array.isArray(tables)) tables = [];
    this.tables$.next(tables);
  }

  private persist(tables: ControlTable[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(tables));
    } catch {}
    this.tables$.next(tables);
  }

  get tables(): Observable<ControlTable[]> {
    return this.tables$.asObservable();
  }

  get tablesSnapshot(): ControlTable[] {
    return this.tables$.value;
  }

  getTable(id: string): ControlTable | undefined {
    return this.tables$.value.find((t) => t.id === id);
  }

  private newId(): string {
    return (
      "ct_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
    );
  }

  createTable(name: string): ControlTable {
    const table: ControlTable = {
      id: this.newId(),
      name: (name || "").trim() || "Nova tabla",
      createdAt: Date.now(),
      rows: [],
    };
    this.persist([...this.tables$.value, table]);
    return table;
  }

  renameTable(id: string, name: string): void {
    const tables = this.tables$.value.map((t) =>
      t.id === id ? { ...t, name: (name || "").trim() || t.name } : t,
    );
    this.persist(tables);
  }

  deleteTable(id: string): void {
    this.persist(this.tables$.value.filter((t) => t.id !== id));
  }

  setRows(id: string, rows: ControlTableRow[]): void {
    const tables = this.tables$.value.map((t) =>
      t.id === id ? { ...t, rows } : t,
    );
    this.persist(tables);
  }

  addSecurity(id: string, row: ControlTableRow): void {
    const table = this.getTable(id);
    if (!table) return;
    if (table.rows.some((r) => r.security.SecIndex === row.security.SecIndex)) {
      return; // već postoji
    }
    this.setRows(id, [...table.rows, row]);
  }

  removeSecurity(id: string, secIndex: number): void {
    const table = this.getTable(id);
    if (!table) return;
    this.setRows(
      id,
      table.rows.filter((r) => r.security.SecIndex !== secIndex),
    );
  }

  private headers() {
    return this._authService.getAuthHeaders("application/json");
  }

  searchSecurities(
    filter: string,
    currency: string,
  ): Observable<TraderSecurityDtoModel[]> {
    return this._http
      .get<TraderSecurityDtoModel[]>(this.apiBase + "/TraderSecurities", {
        headers: this.headers(),
        params: { filter: filter.trim(), currency },
      })
      .pipe(catchError(() => of([])));
  }

  realTimeL1(
    security: ControlTableSecurity,
  ): Observable<EODHDRealTimeResponseModel[]> {
    return this._http
      .post<
        EODHDRealTimeResponseModel[]
      >(this.apiBase + "/TraderRealTimePricesL1", security, { headers: this.headers() })
      .pipe(catchError(() => of([])));
  }

  realTimeL2(
    security: ControlTableSecurity,
  ): Observable<EODHDRealTimeResponseModel[]> {
    return this._http
      .post<
        EODHDRealTimeResponseModel[]
      >(this.apiBase + "/TraderRealTimePricesL2", security, { headers: this.headers() })
      .pipe(catchError(() => of([])));
  }

  eodPricesHistory(
    security: ControlTableSecurity,
    fromDate: Date,
    toDate: Date,
  ): Observable<EODHDEODResponseModel[]> {
    const body = {
      ...security,
      fromDate: this.toLocalISO(fromDate),
      toDate: this.toLocalISO(toDate),
    };
    return this._http
      .post<
        EODHDEODResponseModel[]
      >(this.apiBase + "/TraderEODPricesHistory", body, { headers: this.headers() })
      .pipe(catchError(() => of([])));
  }

  cashBalanceForSecurity(
    security: ControlTableSecurity,
    currency: string,
  ): Observable<number> {
    return this._http
      .post<number>(this.apiBase + "/CashBalanceForSecurity", security, {
        headers: this.headers(),
        params: { currency },
      })
      .pipe(catchError(() => of(0)));
  }

  positionQuantityForSecurity(
    security: ControlTableSecurity,
  ): Observable<number> {
    return this._http
      .post<number>(this.apiBase + "/PositionQuantityForSecurity", security, {
        headers: this.headers(),
      })
      .pipe(catchError(() => of(0)));
  }

  secReservationPercent(secIndex: number): Observable<number> {
    return this._http
      .get<number>(this.apiBase + "/GetSecReservationPercent", {
        headers: this.headers(),
        params: { secIndex: secIndex.toString() },
      })
      .pipe(catchError(() => of(1)));
  }

  allowedTransactionTypes(secStockExchange: number): Observable<number[]> {
    return this._http
      .get<number[]>(this.apiBase + "/GetOrderTranTypeForSE", {
        headers: this.headers(),
        params: { secStockExchange: secStockExchange.toString() },
      })
      .pipe(catchError(() => of([])));
  }

  allowedOrderTypes(
    secStockExchange: number,
    transactionType: number,
  ): Observable<number[]> {
    return this._http
      .get<number[]>(this.apiBase + "/GetOrderTypeForSEAndTranType", {
        headers: this.headers(),
        params: {
          secStockExchange: secStockExchange.toString(),
          orderTransactionType: transactionType.toString(),
        },
      })
      .pipe(catchError(() => of([])));
  }

  orderCheck(order: any): Observable<TraderCheckOrderResponseModel> {
    return this._http.post<TraderCheckOrderResponseModel>(
      this.apiBase + "/TraderOrderCheck",
      order,
      { headers: this.headers() },
    );
  }

  orderInsert(order: any): Observable<TraderOrderResponseModel> {
    return this._http.post<TraderOrderResponseModel>(
      this.apiBase + "/TraderOrderInsert",
      order,
      { headers: this.headers() },
    );
  }

  private toLocalISO(d: Date): string {
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
      d.getHours(),
    )}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-control-table/control-table.service.ts